import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  Button,
  Paper,
  CircularProgress,
  Divider,
  Alert,
  Chip,
  IconButton
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import { apiService } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { normalizeImageUrl } from '../components/Post';
import './BlockedUsers.css';

function BlockedUsers() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [unblockingUsers, setUnblockingUsers] = useState(new Set());

  useEffect(() => {
    fetchBlockedUsers();
  }, []);

  const fetchBlockedUsers = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/api/users/blocked');
      setBlockedUsers(response.data.blocked_users || []);
    } catch (err) {
      console.error('ブロック済みユーザー取得エラー:', err);
      setError('ブロック済みユーザーの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleUnblock = async (userId, username) => {
    if (!window.confirm(`${username}のブロックを解除しますか？`)) {
      return;
    }

    try {
      setUnblockingUsers(prev => new Set([...prev, userId]));
      
      await apiService.post('/api/users/unblock_user', {
        user_id: userId
      });

      // 成功したらリストから削除
      setBlockedUsers(prev => prev.filter(user => user.id !== userId));
    } catch (err) {
      console.error('ブロック解除エラー:', err);
      setError('ブロック解除に失敗しました');
    } finally {
      setUnblockingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  const handleUserClick = (username) => {
    navigate(`/profile/${username}`);
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh' 
      }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', p: 2 }}>
      {/* ヘッダー */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        mb: 3, 
        p: 2,
        borderBottom: '1px solid #e0e0e0'
      }}>
        <IconButton 
          onClick={() => navigate(-1)} 
          sx={{ mr: 2 }}
        >
          <ArrowBackIcon />
        </IconButton>
        <PersonOffIcon sx={{ mr: 1, color: 'text.secondary' }} />
        <Typography variant="h5" component="h1">
          ブロック済みアカウント
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {blockedUsers.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <PersonOffIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            ブロック済みアカウントはありません
          </Typography>
          <Typography variant="body2" color="text.secondary">
            ユーザーをブロックすると、ここに表示されます
          </Typography>
        </Paper>
      ) : (
        <Paper>
          <List>
            {blockedUsers.map((user, index) => (
              <React.Fragment key={user.id}>
                <ListItem sx={{ py: 2 }}>
                  <ListItemAvatar>
                    <Avatar 
                      src={normalizeImageUrl(user.avatar)} 
                      alt={user.display_name}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleUserClick(user.username)}
                    >
                      {user.display_name?.charAt(0) || user.username?.charAt(0)}
                    </Avatar>
                  </ListItemAvatar>
                  
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography 
                          variant="subtitle1" 
                          sx={{ cursor: 'pointer' }}
                          onClick={() => handleUserClick(user.username)}
                        >
                          {user.display_name || user.username}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          @{user.username}
                        </Typography>
                        {user.reason && (
                          <Chip 
                            label={`理由: ${user.reason}`}
                            size="small"
                            variant="outlined"
                            sx={{ mt: 1 }}
                          />
                        )}
                        {user.blocked_at && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            ブロック日時: {new Date(user.blocked_at).toLocaleDateString('ja-JP')}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  
                  <ListItemSecondaryAction>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleUnblock(user.id, user.display_name || user.username)}
                      disabled={unblockingUsers.has(user.id)}
                    >
                      {unblockingUsers.has(user.id) ? (
                        <CircularProgress size={16} />
                      ) : (
                        'ブロック解除'
                      )}
                    </Button>
                  </ListItemSecondaryAction>
                </ListItem>
                
                {index < blockedUsers.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Paper>
      )}
      
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          ブロックしたアカウントは、あなたの投稿を見ることができません
        </Typography>
      </Box>
    </Box>
  );
}

export default BlockedUsers; 